import { requireNodeDefinition, getNodeDefinition } from "../schema/node-registry.js";
import { emitExecChain, hoistValueDepsCore } from "./exec-chain.js";
import { CycleError, topologicalSort } from "./topo-sort.js";
import { buildVariableDeclarationStatement } from "./variable-declarations.js";
import { withNodeComment } from "./node-comment.js";
import { NestedGraphError } from "./nested-graph-error.js";
import { frameForNode } from "../schema/node-display-name.js";
const IDENTIFIER_RE = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
export function sanitizeIdentifier(id) {
    const cleaned = id.replace(/[^A-Za-z0-9_$]/g, "_");
    return /^[0-9]/.test(cleaned) ? `_${cleaned}` : cleaned;
}
/**
 * The identifier another node (in the same blueprint graph, or on the main canvas)
 * references to read one of this node's output values. Dispatches to the node type's own
 * `resultIdentifier` hook (declared on its `NodeDefinition`) rather than a hardcoded type
 * switch — this is what lets new value-producing node types (operators, `logic.graphEntry`,
 * `logic.functionCall`, ...) plug in without editing this file. `handle`
 * is only meaningful for a node with multiple named value outputs (today: only
 * `logic.graphEntry`, one value per function parameter) — every other case ignores it.
 */
export function resultIdentifierFor(node, handle, ctx) {
    const def = requireNodeDefinition(node.type);
    if (!def.resultIdentifier) {
        throw new NestedGraphError(`Node type "${node.type}" produces no reusable value`, [frameForNode(node)]);
    }
    const identifier = def.resultIdentifier(node, handle, ctx);
    if (!IDENTIFIER_RE.test(identifier)) {
        throw new NestedGraphError(`Node "${node.id}" (${node.type}) has an invalid identifier "${identifier}"`, [frameForNode(node)]);
    }
    return identifier;
}
function buildGraphEmitContext(graph, outerVariables = [], promiseExecutorParams, enclosingPromiseExecutorParams) {
    // Filter out comment nodes and other nodes with invalid types (UI-only annotations, not executable)
    const validNodes = graph.nodes.filter((n) => {
        if (!n.type)
            return false;
        const def = getNodeDefinition(n.type);
        return def !== undefined;
    });
    const nodesById = new Map(validNodes.map((n) => [n.id, n]));
    const cache = new Map();
    const matchesHandle = (edge, side, handle) => handle === undefined || (side === "source" ? edge.sourceHandle : edge.targetHandle) === handle;
    // A `variable.get`/`variable.set` node's `resultIdentifier`/`emit()` look up `data.variableId`
    // against `ctx.flow.variables` — merging this graph's own local variables with the outer
    // module-level ones (local wins on id collision, though ids are UUIDs so collisions in
    // practice never happen) lets a Handler Function's blueprint graph read/write module-level
    // state declared via the main canvas's Variables panel, not just its own function-local
    // variables. The outer variables are NOT re-declared here — `emit-express.ts` already emits
    // their module-level `const`/`let`/`var` statement once; only THIS graph's own `variables`
    // get a declaration statement inside the compiled function body (see below).
    const mergedVariables = [...(graph.variables ?? []), ...outerVariables];
    const ctx = {
        // Function-graph nodes never reference the outer/top-level Flow — this stub satisfies
        // EmitContext's shape without pretending the nested graph is a real top-level flow.
        flow: { version: "1", meta: { name: "", target: "express" }, nodes: graph.nodes, edges: graph.edges, variables: mergedVariables },
        getNode: (nodeId) => nodesById.get(nodeId),
        getIncoming: (nodeId, handle) => graph.edges.filter((e) => e.target === nodeId && matchesHandle(e, "target", handle)),
        getOutgoing: (nodeId, handle) => graph.edges.filter((e) => e.source === nodeId && matchesHandle(e, "source", handle)),
        promiseExecutorParams,
        enclosingPromiseExecutorParams,
        emitNode: (nodeId) => {
            const cached = cache.get(nodeId);
            if (cached)
                return cached;
            const node = nodesById.get(nodeId);
            if (!node)
                throw new NestedGraphError(`emitNode: unknown node id "${nodeId}"`);
            const def = requireNodeDefinition(node.type);
            try {
                const emitted = withNodeComment(node, def.emit(node, ctx));
                cache.set(nodeId, emitted);
                return emitted;
            }
            catch (err) {
                if (err instanceof NestedGraphError)
                    throw err;
                throw new NestedGraphError(err instanceof Error ? err.message : String(err), [frameForNode(node)]);
            }
        },
    };
    return ctx;
}
/**
 * Compiles a Function node's blueprint body graph into a JS function-body string. Trusts the
 * graph as given — `validate.ts` is responsible for checking things like "every edge wired
 * from the `logic.graphEntry` node names a real parameter" before this ever runs.
 *
 * The trunk is compiled by the same recursive, branch-aware walker used for the main
 * canvas's route handler chains (`exec-chain.ts`), starting from whatever the entry node's
 * "out" pin is wired to — this is what lets a Branch/Switch node placed inside a function's
 * blueprint graph compile into a real `if`/`switch` block instead of flat statement soup.
 *
 * `promiseExecutorParams`, when passed, marks this graph as a `logic.promise` node's
 * blueprint-mode executor body — it's this graph's `logic.graphEntry`'s unique per-instance
 * `resolve`/`reject` identifiers (see `exec-chain.ts`'s `promiseExecutorParamNames`), so a
 * Callback wired to this entry's "resolve"/"reject" pin resolves to the same identifier the
 * enclosing `new Promise((resolve_X, reject_X) => ...)` actually declares, instead of the bare
 * literal "resolve"/"reject" that would collide with a `logic.promise` node nested inside this
 * very graph.
 *
 * `enclosingPromiseExecutorParams`, when passed, is every ANCESTOR `logic.promise` executor
 * scope enclosing this graph (see `exec-chain.ts`'s `mergeEnclosingPromiseParams`) — lets this
 * graph's `logic.graphEntry` resolve "outerResolve"/"outerReject" (and numbered-suffix
 * variants for deeper nesting) to an outer Promise's own resolve/reject.
 */
export function emitFunctionGraphBody(graph, outerVariables = [], promiseExecutorParams, enclosingPromiseExecutorParams) {
    // Filter out comment nodes and other nodes with invalid types (UI-only annotations, not executable)
    const validNodes = graph.nodes.filter((n) => {
        if (!n.type)
            return false;
        const def = getNodeDefinition(n.type);
        return def !== undefined;
    });
    const returnNodes = validNodes.filter((n) => n.type === "logic.graphReturn");
    // A whole-graph cycle pre-flight, independent of the reachability-based walk below: since
    // exec-chain.ts's walker only visits nodes actually reachable from the entry's exec spine
    // or hoisted as a value dependency, a cycle among nodes that happen to be otherwise
    // unreferenced (dead code) would never be visited and so would never be caught by the
    // walker's own cycle check. This whole-graph check (order is discarded — only used to
    // detect a cycle) preserves the "no cycles anywhere in this graph" guarantee regardless of
    // reachability. Return nodes are included here same as everything else — with `outputs: []`
    // they can never actually participate in a cycle.
    try {
        topologicalSort(validNodes.map((n) => n.id), graph.edges);
    }
    catch (err) {
        if (err instanceof CycleError) {
            const cycleNode = validNodes.find((n) => n.id === err.remainingNodeIds[0]);
            throw new NestedGraphError("This function's blueprint graph contains a cycle", cycleNode ? [frameForNode(cycleNode)] : []);
        }
        throw err;
    }
    const ctx = buildGraphEmitContext(graph, outerVariables, promiseExecutorParams, enclosingPromiseExecutorParams);
    const entry = validNodes.find((n) => n.type === "logic.graphEntry");
    const startNodeId = entry ? ctx.getOutgoing(entry.id, "out")[0]?.target : undefined;
    let trunk;
    try {
        trunk = emitExecChain(startNodeId, ctx);
    }
    catch (err) {
        if (err instanceof NestedGraphError)
            throw err;
        throw new NestedGraphError(err instanceof Error ? err.message : String(err));
    }
    const statements = [];
    // Function-scoped variable declarations go at the very top of the compiled function body —
    // JS's native function-scope (or module-scope, for the main-canvas equivalent in
    // emit-express.ts) already gives "block scope unless var" for free just from where the
    // declaration is textually emitted, no extra runtime engineering needed.
    for (const variable of graph.variables ?? []) {
        try {
            const statement = buildVariableDeclarationStatement(variable);
            if (statement)
                statements.push(statement);
        }
        catch (err) {
            throw new NestedGraphError(err instanceof Error ? err.message : String(err), [
                { nodeId: variable.id, nodeType: "variable-declaration", label: `Variable "${variable.name}"` },
            ]);
        }
    }
    const imports = [...trunk.imports];
    if (trunk.code)
        statements.push(trunk.code);
    // Return nodes with no incoming "In" edge are the backward-compat fallback: the shape every
    // Return node had before it gained an exec pin at all. Each is appended as a trailing
    // `return <value>;` after the whole compiled trunk, exactly like before this feature
    // existed — a Return WITH an "In" edge was already emitted in place by `emitExecChain`'s
    // walk above and must not be touched again here. Reuses the Return node's own `emit()`
    // (via `ctx.emitNode`) rather than re-deriving the return statement by hand, so the
    // in-place and fallback paths can never disagree about how a Return compiles.
    const unwiredReturns = returnNodes.filter((n) => ctx.getIncoming(n.id, "in").length === 0);
    for (const returnNode of unwiredReturns) {
        try {
            // Reuses the trunk's `emitted` set so a value already hoisted for a trunk-level
            // consumer isn't redeclared here.
            const hoisted = hoistValueDepsCore(returnNode.id, ctx, trunk.emitted);
            statements.push(...hoisted.statements);
            imports.push(...hoisted.imports);
            const emitted = ctx.emitNode(returnNode.id);
            if (emitted.body)
                statements.push(emitted.body);
            if (emitted.imports)
                imports.push(...emitted.imports);
        }
        catch (err) {
            if (err instanceof NestedGraphError)
                throw err;
            throw new NestedGraphError(err instanceof Error ? err.message : String(err), [frameForNode(returnNode)]);
        }
    }
    return { code: statements.join("\n"), imports, requiresAsync: trunk.requiresAsync };
}
//# sourceMappingURL=emit-function-graph.js.map